import { useMemo } from 'react'
import { convertMarkdownToHtml, safeRenderHtml } from './htmlUtils'

interface ContentViewerProps {
  content: string | null
  maxLength?: number
  className?: string
  renderHtml?: boolean // Si true, renderiza HTML real en lugar de solo texto
}

export default function ContentViewer({ 
  content, 
  maxLength = 200, 
  className = "text-sm text-gray-600",
  renderHtml = false
}: ContentViewerProps) {
  const { textContent, htmlContent } = useMemo(() => {
    if (!content) return { textContent: '', htmlContent: '' }
    
    let processedText = content
    let processedHtml = content
    
    if (renderHtml) {
      // Convertir a HTML para renderizado
      processedHtml = convertMarkdownToHtml(content)
      
      // También preparar texto limpio para fallback/truncamiento
      processedText = content
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&hellip;/g, '...')
        .replace(/&mdash;/g, '—')
        .replace(/&ndash;/g, '–')
        .replace(/&laquo;/g, '«')
        .replace(/&raquo;/g, '»')
        .replace(/\s+/g, ' ')
        .replace(/\n\s*\n/g, '\n')
        .trim()
    } else {
      // Solo texto plano
      processedText = content
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&hellip;/g, '...')
        .replace(/&mdash;/g, '—')
        .replace(/&ndash;/g, '–')
        .replace(/&laquo;/g, '«')
        .replace(/&raquo;/g, '»')
        .replace(/\s+/g, ' ')
        .replace(/\n\s*\n/g, '\n')
        .trim()
    }
    
    // Truncar si es necesario
    if (processedText.length > maxLength) {
      const truncated = processedText.substring(0, maxLength)
      const lastSpace = truncated.lastIndexOf(' ')
      processedText = (lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated) + '...'
    }
    
    return { textContent: processedText, htmlContent: processedHtml }
  }, [content, maxLength, renderHtml])

  if (!content) {
    return (
      <div className={className}>
        Sin contenido
      </div>
    )
  }

  // Si se debe renderizar HTML, usar dangerouslySetInnerHTML
  if (renderHtml) {
    return (
      <div 
        className={className}
        dangerouslySetInnerHTML={safeRenderHtml(htmlContent)}
      />
    )
  }

  return (
    <div className={className}>
      {textContent}
    </div>
  )
}